import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import LetterBodyView from "@/components/LetterBodyView";
import {
  Inbox,
  CheckCircle,
  XCircle,
  FileText,
  CalendarDays,
  Loader2,
  AlertCircle,
  Eye,
} from "lucide-react";

export default function Approvals() {
  const [inbox, setInbox] = useState({ leaves: [], letters: [], as_hr_admin: false, as_manager: false });
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [viewingLetter, setViewingLetter] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [i, e, lt] = await Promise.all([
        api.get("/approvals/inbox"),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get("/leave-types").catch(() => ({ data: [] })),
      ]);
      setInbox(i.data || { leaves: [], letters: [] });
      setEmployees(e.data || []);
      setLeaveTypes(lt.data || []);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const empMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ")])),
    [employees],
  );
  const ltMap = useMemo(
    () => Object.fromEntries(leaveTypes.map((t) => [t.id, t.name])),
    [leaveTypes],
  );

  const decideLeave = async (id, action) => {
    setBusy(`leave-${id}`);
    setErr("");
    try {
      const note = action === "Rejected" ? (window.prompt("Reason (optional):") || "") : "";
      await api.patch(`/leaves/${id}/decision`, { action, note });
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    } finally {
      setBusy("");
    }
  };

  const decideLetter = async (id, decision) => {
    setBusy(`letter-${id}`);
    setErr("");
    try {
      const note = decision === "Rejected" ? (window.prompt("Reason (optional):") || "") : "";
      await api.post(`/letters/${id}/approve`, { decision, note, stage: "hr_admin" });
      setViewingLetter(null);
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    } finally {
      setBusy("");
    }
  };

  const total = inbox.leaves.length + inbox.letters.length;

  return (
    <AdminLayout title="My Approvals" breadcrumbs={["Home", "My Approvals"]}>
      <div className="mb-4 flex items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-full font-medium">
          <Inbox className="h-4 w-4" /> {total} pending
        </span>
        {inbox.as_hr_admin && <span className="text-xs px-2 py-1 bg-sky-50 border border-sky-200 text-sky-700 rounded">HR Admin view</span>}
        {inbox.as_manager && <span className="text-xs px-2 py-1 bg-purple-50 border border-purple-200 text-purple-700 rounded">Manager view</span>}
      </div>

      {err && (
        <div className="mb-3 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {err}
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-slate-200 rounded p-10 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin inline" /> Loading…
        </div>
      ) : total === 0 ? (
        <div className="bg-white border border-slate-200 rounded p-10 text-center text-slate-400">
          Nothing pending. You&apos;re all caught up.
        </div>
      ) : (
        <div className="space-y-6">
          {inbox.leaves.length > 0 && (
            <Section title={`Leave Requests (${inbox.leaves.length})`} icon={CalendarDays}>
              <ul className="divide-y divide-slate-100" data-testid="approval-leaves">
                {inbox.leaves.map((lv) => (
                  <li key={lv.id} className="px-4 py-3 flex items-start justify-between gap-3 text-sm" data-testid={`leave-row-${lv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800">{empMap[lv.employee_id] || lv.employee_id}</div>
                      <div className="text-xs text-slate-600">
                        {ltMap[lv.leave_type_id] || "Leave"} · {formatDisplayDate(lv.start_date)} → {formatDisplayDate(lv.end_date)} ({lv.days} day{lv.days === 1 ? "" : "s"})
                      </div>
                      {lv.reason && <div className="text-xs text-slate-500 mt-0.5 italic">&ldquo;{lv.reason}&rdquo;</div>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => decideLeave(lv.id, "Rejected")} disabled={busy === `leave-${lv.id}`} className="px-3 py-1.5 text-xs border border-rose-200 text-rose-700 rounded hover:bg-rose-50" data-testid={`reject-leave-${lv.id}`}>
                        <XCircle className="h-3.5 w-3.5 inline mr-1" /> Reject
                      </button>
                      <button onClick={() => decideLeave(lv.id, "Approved")} disabled={busy === `leave-${lv.id}`} className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded" data-testid={`approve-leave-${lv.id}`}>
                        <CheckCircle className="h-3.5 w-3.5 inline mr-1" /> Approve
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {inbox.letters.length > 0 && (
            <Section title={`HR Letters awaiting approval (${inbox.letters.length})`} icon={FileText}>
              <ul className="divide-y divide-slate-100" data-testid="approval-letters">
                {inbox.letters.map((lt) => (
                  <li key={lt.id} className="px-4 py-3 flex items-start justify-between gap-3 text-sm" data-testid={`letter-row-${lt.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800">{lt.type} — {lt.employee_name}</div>
                      <div className="text-xs text-slate-600">
                        Created by {lt.issued_by_name} on {formatDisplayDate(lt.issue_date)}
                        {lt.letter_number ? ` · ${lt.letter_number}` : ""}
                      </div>
                      <div className="text-xs mt-1">
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Pending Approval</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => setViewingLetter(lt)}
                        disabled={!!busy}
                        className="px-3 py-1.5 text-xs border border-slate-200 text-slate-700 rounded hover:bg-slate-50"
                        data-testid={`view-letter-${lt.id}`}
                      >
                        <Eye className="h-3.5 w-3.5 inline mr-1" /> View
                      </button>
                      <button
                        onClick={() => decideLetter(lt.id, "Rejected")}
                        disabled={!!busy}
                        className="px-3 py-1.5 text-xs border border-rose-200 text-rose-700 rounded hover:bg-rose-50"
                        data-testid={`reject-letter-${lt.id}`}
                      >
                        <XCircle className="h-3.5 w-3.5 inline mr-1" /> Reject
                      </button>
                      <button
                        onClick={() => decideLetter(lt.id, "Approved")}
                        disabled={!!busy}
                        className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                        data-testid={`approve-letter-${lt.id}`}
                      >
                        <CheckCircle className="h-3.5 w-3.5 inline mr-1" /> Approve
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {viewingLetter && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="letter-approval-preview">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{viewingLetter.title || viewingLetter.type}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {viewingLetter.employee_name}
                  {viewingLetter.employee_code ? ` (${viewingLetter.employee_code})` : ""}
                  {" · "}
                  {formatDisplayDate(viewingLetter.issue_date)}
                </p>
              </div>
              <button type="button" onClick={() => setViewingLetter(null)} className="p-2 text-slate-400 hover:bg-slate-100 rounded">✕</button>
            </div>
            <div className="px-6 pt-3">
              <div className="text-[11px] font-semibold tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                PENDING APPROVAL — preview only · not for distribution · signature applied on Issue
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              {viewingLetter.letter_number ? (
                <div className="font-mono text-xs text-slate-500 mb-3">Letter No: {viewingLetter.letter_number}</div>
              ) : null}
              <LetterBodyView body={viewingLetter.body} letter={viewingLetter} className="font-serif" />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-[#f9fafb] flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setViewingLetter(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => decideLetter(viewingLetter.id, "Rejected")}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-rose-200 text-rose-700 rounded hover:bg-rose-50 disabled:opacity-60"
              >
                <XCircle className="h-4 w-4" /> Reject
              </button>
              <button
                type="button"
                onClick={() => decideLetter(viewingLetter.id, "Approved")}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium disabled:opacity-60"
              >
                {busy === `letter-${viewingLetter.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Approve &amp; Issue
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm">
      <div className="px-4 py-2.5 border-b border-slate-200 text-sm font-semibold text-slate-800 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#226DB4]" /> {title}
      </div>
      {children}
    </div>
  );
}
